import { useSelector } from "react-redux";
import USUARIO_PERFIL from "../enums/USUARIO_PERFIL";

/**
 * Recupera o perfil atual do usuário e expõe utilitários de classificação.
 *
 * @description
 * Este hook realiza as seguintes operações:
 * 1. Lê o usuário atual da store Redux
 * 2. Resolve os metadados do perfil a partir de `USUARIO_PERFIL`
 * 3. Calcula a prioridade do perfil
 * 4. Expõe flags booleanas para os perfis suportados
 *
 * @returns {Object} Dados do usuário e atalhos relacionados ao perfil
 * @returns {Object} returns.usuario Objeto do usuário atual na store
 * @returns {string | undefined} returns.perfil Identificador bruto do perfil do usuário
 * @returns {Object | null} returns.perfilData Metadados completos do perfil encontrado
 * @returns {Object | undefined} returns.usuarioPerfil Configuração do perfil indexada por identificador
 * @returns {number} returns.usuarioPrioridade Prioridade numérica do perfil
 * @returns {boolean} returns.isAdmin Indica se o perfil atual é administrador
 * @returns {boolean} returns.isSuperUser Indica se o perfil atual é superusuário
 * @returns {boolean} returns.isOperador Indica se o perfil atual é operador
 * @returns {boolean} returns.isUserPadrao Indica se o perfil atual é usuário padrão
 * @returns {boolean} returns.isConsulta Indica se o perfil atual é de consulta
 */
function usePerfil() {
  // Recupera o usuário da store
  const xUser = useSelector((store) => store.user);

  // Obtém o perfil do usuário (pode ser string ou objeto)
  const xPerfil = xUser?.perfil;

  const xUsuarioPerfil = xPerfil && USUARIO_PERFIL[xPerfil];
  const xUsuarioPrioridade = xUsuarioPerfil ? xUsuarioPerfil.prioridade : 9;

  // Checa por tipo específico de perfil
  const isAdmin = xPerfil === USUARIO_PERFIL.ad.id;
  const isSuperUser = xPerfil === USUARIO_PERFIL.sp.id;
  const isOperador = xPerfil === USUARIO_PERFIL.op.id;
  const isUserPadrao = xPerfil === USUARIO_PERFIL.us.id;
  const isConsulta = xPerfil === USUARIO_PERFIL.cs.id;

  // Também pode retornar o objeto de perfil correspondente e outros utilitários
  const xPerfilData =
    Object.values(USUARIO_PERFIL).find(
      (xPerfilObj) => xPerfilObj.id === xPerfil
    ) || null;

  return {
    usuario: xUser,
    perfil: xPerfil,
    perfilData: xPerfilData,
    usuarioPerfil: xUsuarioPerfil,
    usuarioPrioridade: xUsuarioPrioridade,
    isAdmin,
    isSuperUser,
    isOperador,
    isUserPadrao,
    isConsulta,
  };
}

export default usePerfil;
